export const HOLD_YEARS = 3;

// 默认权重（可被用户覆盖）
export const DEFAULT_WEIGHTS = { narrative: 0.20, tech: 0.20, team: 0.18, tokenomics: 0.16, data: 0.14, compliance: 0.12 };

// ══════════════════════════════════════════════════════
//  阶段化评估框架：早期/中期/后期权重自动切换
// ══════════════════════════════════════════════════════
export const STAGE_WEIGHTS = {
  early: {
    narrative: 0.30, team: 0.28, tokenomics: 0.18, tech: 0.12, compliance: 0.10, data: 0.02,
    label: "早期阶段", label_en: "Early Stage",
    focus: "叙事 + 团队", focus_en: "Narrative + Team",
    desc: "项目尚无产品或链上数据，重点评估市场叙事、团队背景、代币设计合理性。数据维度不参与评分。",
    desc_en: "No verifiable product or on-chain data yet. Focus on narrative, team, and token design rationale.",
  },
  growth: {
    narrative: 0.18, tech: 0.25, team: 0.15, tokenomics: 0.15, data: 0.18, compliance: 0.09,
    label: "产品阶段", label_en: "Product Stage",
    focus: "产品 + 数据", focus_en: "Product + Data",
    desc: "项目已有可验证的产品和链上数据，进入全维度评估，数据表现和技术产品权重上升。",
    desc_en: "Verifiable product signals present. Full-dimension evaluation; tech and data weights rise.",
  },
  tge_post: {
    tokenomics: 0.30, data: 0.28, tech: 0.14, narrative: 0.12, team: 0.08, compliance: 0.08,
    label: "TGE 后", label_en: "Post-TGE",
    focus: "代币指标 + 数据", focus_en: "Tokenomics + Data",
    desc: "代币已上市，重点关注代币经济（FDV/解锁压力/流通情况）和市场数据（交易量/价格表现）。",
    desc_en: "Token is live. Focus on tokenomics (FDV/unlock/circulation) and market data (volume/price).",
  },
};

// 基于多信号自动判断项目阶段，返回 { stage, signals, confidence }
export function detectStage(raw) {
  const { details, cgData, defiData, webIntel } = raw;
  const signals = { tge: [], growth: [], early: [] };
  const signals_en = { tge: [], growth: [], early: [] };

  // ── TGE 后信号 ────────────────────────────────────────────────────
  const cgPrice = cgData?.market_data?.current_price?.usd || 0;
  const cgFdv   = cgData?.market_data?.fully_diluted_valuation?.usd || 0;
  const tokenSym = details?.token_symbol || "";

  if (tokenSym && cgPrice > 0 && cgFdv > 0) {
    signals.tge.push(`代币 $${tokenSym} 已在 CoinGecko 有价格数据（FDV ${fmt(cgFdv)}）`);
    signals_en.tge.push(`Token $${tokenSym} has CoinGecko price data (FDV ${fmt(cgFdv)})`);
  }
  if (tokenSym && cgData?.market_data?.total_volume?.usd > 0) {
    signals.tge.push(`CoinGecko 有 24h 交易量`);
    signals_en.tge.push(`CoinGecko 24h trading volume exists`);
  }

  // ── 产品阶段信号 ──────────────────────────────────────────────────
  if (defiData?.protocol) {
    signals.growth.push(`DeFiLlama 已收录协议「${defiData.protocol.name}」`);
    signals_en.growth.push(`DeFiLlama lists protocol "${defiData.protocol.name}"`);
  }
  if ((defiData?.tvl?.current || 0) > 0) {
    signals.growth.push(`DeFiLlama TVL ${fmt(defiData.tvl.current)}`);
    signals_en.growth.push(`DeFiLlama TVL ${fmt(defiData.tvl.current)}`);
  }

  // RootData 偶尔返回 { contract_platform:"", contract_address:"" } 占位项，
  // 会把还没上线的早期项目误判为产品阶段；这里按地址长度过滤掉空/明显非法的合约。
  const validContracts = (details?.contracts || []).filter(c => {
    const addr = (c?.contract_address || '').trim();
    return addr.length >= 10 && addr !== '0x0000000000000000000000000000000000000000';
  });
  if (validContracts.length > 0) {
    signals.growth.push(`RootData 已记录 ${validContracts.length} 个合约地址`);
    signals_en.growth.push(`RootData lists ${validContracts.length} contract address(es)`);
  }

  const techStage = webIntel?.techIntel?.product_stage || "";
  const techStageEn = webIntel?.techIntel?.product_stage_en || techStage;
  if (techStage && !techStage.includes("白皮书")) {
    signals.growth.push(`技术分析：产品阶段为「${techStage}」`);
    signals_en.growth.push(`Tech analysis: Product stage is "${techStageEn}"`);
  }

  const desc = ((details?.description || "") + " " + (details?.one_liner || "")).toLowerCase();
  const GROWTH_KEYWORDS = ["testnet", "mainnet", "launched", "live", "beta", "v1", "v2", "测试网", "主网", "已上线", "已发布", "production", "deployed"];
  const foundKeywords = GROWTH_KEYWORDS.filter(k => desc.includes(k));
  if (foundKeywords.length > 0) {
    signals.growth.push(`描述包含产品关键词: ${foundKeywords.slice(0, 3).join(", ")}`);
    signals_en.growth.push(`Description contains product keywords: ${foundKeywords.slice(0, 3).join(", ")}`);
  }

  if (tokenSym && cgPrice === 0) {
    signals.tge.push(`已发行代币 $${tokenSym}（未在 CoinGecko 收录，可能在 DEX 上市）`);
    signals_en.tge.push(`Token $${tokenSym} issued (not on CoinGecko, may be on DEX)`);
  }

  const tags = (details?.tags || []).map(t => t.toLowerCase());
  const isChain = tags.some(t => ["layer1","layer 1","l1","layer2","layer 2","l2","blockchain","public chain","公链","evm","rollup"].includes(t));
  if (isChain) {
    if (signals.growth.length > 0) {
      signals.growth.push(`公链/L1/L2 项目，有产品信号，判定为产品阶段`);
      signals_en.growth.push(`L1/L2/Chain project with product signals, classified as Product Stage`);
    }
  }

  if (signals.tge.length > 0 && (cgPrice > 0 || (tokenSym && cgFdv > 0))) {
    return { stage: "tge_post", signals: signals.tge, signals_en: signals_en.tge, growthSignals: signals.growth };
  }

  if (signals.growth.length > 0) {
    return { stage: "growth", signals: signals.growth, signals_en: signals_en.growth };
  }

  signals.early.push("暂无链上数据、合约部署记录或产品阶段描述");
  signals_en.early.push("No on-chain data, contract deployments, or product stage signals found");
  if (tokenSym) {
    signals.early.push(`代币 $${tokenSym} 尚未上市`);
    signals_en.early.push(`Token $${tokenSym} not yet listed`);
  }
  return { stage: "early", signals: signals.early, signals_en: signals_en.early };
}

// 根据用户权重生成 DIMS（保持颜色不变）
export function buildDims(weights = DEFAULT_WEIGHTS) {
  const COLORS     = { narrative: "#3b82f6", tech: "#10b981", team: "#f59e0b", tokenomics: "#8b5cf6", data: "#ec4899", compliance: "#ef4444" };
  const LABELS     = { narrative: "叙事与愿景", tech: "技术与产品", team: "团队背景", tokenomics: "代币经济", data: "数据表现", compliance: "合规风险" };
  const SHORTS     = { narrative: "叙事", tech: "技术", team: "团队", tokenomics: "代币", data: "数据", compliance: "合规" };
  const LABELS_EN  = { narrative: "Narrative & Vision", tech: "Tech & Product", team: "Team", tokenomics: "Tokenomics", data: "On-chain Data", compliance: "Compliance" };
  const SHORTS_EN  = { narrative: "Narrative", tech: "Tech", team: "Team", tokenomics: "Token", data: "Data", compliance: "Compliance" };
  return Object.keys(DEFAULT_WEIGHTS).map(key => ({
    key, label: LABELS[key], short: SHORTS[key],
    label_en: LABELS_EN[key], short_en: SHORTS_EN[key],
    w: weights[key] ?? DEFAULT_WEIGHTS[key], color: COLORS[key],
  }));
}

// 用自定义权重计算 composite
export function calcComposite(scores, weights = DEFAULT_WEIGHTS) {
  return Math.round(Object.keys(DEFAULT_WEIGHTS).reduce((s, k) => s + (scores?.[k] || 0) * (weights[k] ?? DEFAULT_WEIGHTS[k]), 0));
}

// 综合分的唯一权威计算入口：有账户级自定义权重覆盖时统一使用；
// 否则用该项目分析当时保存的阶段自适应权重（project.analysis.stage_weights），
// 而不是重新调用 detectStage —— 保证和 project.analysis.composite 原值一致，
// 不会因为 STAGE_WEIGHTS 后续调整而让老分析显示出跟当初不同的分数。
export function effectiveComposite(project, overrideWeights) {
  const weights = overrideWeights || project?.analysis?.stage_weights || DEFAULT_WEIGHTS;
  return calcComposite(project?.analysis?.scores, weights);
}

export function rating(s) {
  if (s >= 90) return { label: "S", fg: "#059669", bg: "#ecfdf5", border: "#6ee7b7" };
  if (s >= 80) return { label: "A", fg: "#2563eb", bg: "#eff6ff", border: "#93c5fd" };
  if (s >= 70) return { label: "B", fg: "#d97706", bg: "#fffbeb", border: "#fcd34d" };
  if (s >= 60) return { label: "C", fg: "#ea580c", bg: "#fff7ed", border: "#fdba74" };
  return             { label: "D", fg: "#dc2626", bg: "#fef2f2", border: "#fca5a5" };
}

export function fmt(n, d = 1) {
  if (!n) return "—";
  if (n >= 1e9) return `$${(n / 1e9).toFixed(d)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(d)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(d)}K`;
  return `$${n.toFixed(d)}`;
}

export const IRR_SCENARIOS = [
  { label: "牛市", label_en: "Bull",   multiple: 3.0, prob: 0.30, color: "#10b981" },
  { label: "中性", label_en: "Base",   multiple: 1.5, prob: 0.50, color: "#f59e0b" },
  { label: "熊市", label_en: "Bear",   multiple: 0.8, prob: 0.20, color: "#ef4444" },
];

export function calcIRR(entryVal, fairVal, holdYears = HOLD_YEARS) {
  if (!entryVal || !fairVal) return null;
  const underMult = fairVal / entryVal;
  const scenarios = IRR_SCENARIOS.map(s => {
    const exitMult = underMult * s.multiple;
    const irr = Math.pow(Math.max(exitMult, 0.01), 1 / holdYears) - 1;
    return { ...s, exitMult, irr };
  });
  const expectedIRR = scenarios.reduce((a, s) => a + s.prob * s.irr, 0);
  const expectedMult = scenarios.reduce((a, s) => a + s.prob * s.exitMult, 0);
  return { scenarios, expectedIRR, expectedMult, underMult };
}

// ── 叙事×链上四象限计算 ────────────────────────────────
export function calcNarrativeChainQuadrant(project, surfMindshare = null) {
  const { cgData, defiData, duneTokenInfo, duneAnalytics } = project;

  // ── 叙事热度得分 (0-100) ──────────────────────────────
  let narrativeScore = 0;
  const sentUp    = cgData?.sentiment_votes_up_percentage ?? null;
  const tw        = cgData?.community_data?.twitter_followers ?? 0;
  const watchlist = cgData?.watchlist_portfolio_users ?? 0;

  if (sentUp != null) narrativeScore += sentUp * 0.40;
  if (tw > 0)        narrativeScore += Math.min(30, Math.log10(tw) / Math.log10(1_000_000) * 30);
  if (watchlist > 0) narrativeScore += Math.min(30, Math.log10(watchlist) / Math.log10(500_000) * 30);

  // Surf 7日 mindshare 动量：最近7日 vs 前7日，贡献 -5 ~ +8 分
  if (surfMindshare?.data?.length > 14) {
    const ms     = surfMindshare.data;
    const recent = ms.slice(-7).reduce((s, d) => s + (d.value || 0), 0) / 7;
    const prev   = ms.slice(-14, -7).reduce((s, d) => s + (d.value || 0), 0) / 7;
    if (prev > 0) {
      const momentum = (recent - prev) / prev;
      narrativeScore += Math.min(8, Math.max(-5, momentum * 25));
    }
  }
  narrativeScore = Math.min(100, Math.round(narrativeScore));

  // ── 链上活跃度得分 (0-100) ──────────────────────────────
  let chainScore = 0;
  const tvl        = defiData?.protocol?.tvl ?? 0;
  const dailyUsers = defiData?.users?.daily   ?? 0;
  const poolSize   = duneTokenInfo?.poolSize   ?? 0;

  // Dune 30日 DEX 数据
  const vol30d = duneAnalytics?.total_volume ?? 0;
  const duneDays = duneAnalytics?.days || [];
  const avgTraders = duneDays.length > 0
    ? duneDays.reduce((s, d) => s + (d.unique_traders || 0), 0) / duneDays.length
    : 0;

  // 重新分配权重：TVL 35% + 日活 25% + 流动性 15% + Dune成交量 15% + 日均交易者 10%
  if (tvl > 0)        chainScore += Math.min(35, Math.log10(tvl)        / Math.log10(1_000_000_000) * 35);
  if (dailyUsers > 0) chainScore += Math.min(25, Math.log10(dailyUsers) / Math.log10(1_000_000)     * 25);
  if (poolSize > 0)   chainScore += Math.min(15, Math.log10(poolSize)   / Math.log10(100_000_000)   * 15);
  if (vol30d > 0)     chainScore += Math.min(15, Math.log10(vol30d)     / Math.log10(1_000_000_000) * 15);
  if (avgTraders > 0) chainScore += Math.min(10, Math.log10(avgTraders) / Math.log10(100_000)       * 10);
  chainScore = Math.min(100, Math.round(chainScore));

  // ── 象限判断 ──────────────────────────────────────────
  const hiN = narrativeScore >= 50;
  const hiC = chainScore     >= 50;
  const QUADS = {
    "11": { key: "resonance",   label: "共振",     label_en: "Resonance",    emoji: "🟢", color: "#10b981", bg: "rgba(16,185,129,0.08)",  desc: "叙事热度与链上活跃一致，强信号，值得深入研究",          desc_en: "Narrative and on-chain activity aligned — strong signal, worth deep research" },
    "01": { key: "alpha",       label: "潜在Alpha", label_en: "Alpha Signal", emoji: "🔵", color: "#3b82f6", bg: "rgba(59,130,246,0.08)",  desc: "市场尚未充分定价，但用户已在真实使用，Alpha 窗口",     desc_en: "Underpriced by market but already used on-chain — Alpha window open" },
    "10": { key: "hype",        label: "炒作预警",  label_en: "Hype Warning", emoji: "🟡", color: "#f59e0b", bg: "rgba(245,158,11,0.08)",  desc: "叙事热度高但链上数据未跟上，警惕纯叙事驱动风险",        desc_en: "High narrative heat without on-chain follow-through — watch for narrative-only risk" },
    "00": { key: "noise",       label: "无信号",    label_en: "No Signal",   emoji: "⚪", color: "#64748b", bg: "rgba(100,116,139,0.06)", desc: "叙事与链上均弱，建议持续观察后再评估",                  desc_en: "Weak on both narrative and on-chain — watch and re-evaluate later" },
  };
  const quad = QUADS[`${hiN ? 1 : 0}${hiC ? 1 : 0}`];

  return { narrativeScore, chainScore, ...quad };
}

export function alphaScore(composite, irrData, riskPenalty = 0) {
  if (!irrData) return 0;
  const qualityFactor = composite / 100;
  const cappedIRR = Math.min(irrData.expectedIRR, 1.5);
  const adj = cappedIRR * irrData.underMult * qualityFactor * (1 - riskPenalty);
  return Math.min(Math.max(Math.round(adj * 100), 0), 300);
}
